import { Avatar, Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import SubmitButton from "../../components/SubmitButton";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { useSelector } from "react-redux";
import { colors } from "../../common/colors";
import appServices from "../../services/app-services/appServices";
import axios, { CancelTokenSource } from "axios";
import { convertUserImage, getRole, isGroupLeader, isMemberRole, isSubLeaderRole } from "../../util/utils";

const AddPostUser = () => {
  const navigate = useNavigate();
  const state = useLocation().state;
  const [postUsers, setPostUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [showMessage, setShowMessage] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const { user } = useSelector((state: any) => state.userAuth);
  const [iconImage, setIconImage] = useState<any>();
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const getImageSrc = () => {
    if (state.item?.is_anonymous === 3) {
      setIconImage("");
    } else if (state.item?.member_group) {
      const group = state.item?.member_group
      appServices.getGroupIcon(group.id).then(blob => {
        const file = new File([blob], group.group_icon, { type: blob.type });
        setIconImage(URL.createObjectURL(file));
      }).catch(() => {
        console.error("Failed to get group icon image")
        setIconImage('');
      })
    } else if (state.item?.creator?.image) {
      setIconImage(convertUserImage(state.item?.creator?.image));
    } else {
      setIconImage("");
    }
  };

  const getName = () => {
    return state.item?.creator?.nickname || state.item?.creator?.name || " ";
  };

  useEffect(() => {
    if (state.item?.member_group) {
      setLoading(true);
      appServices.getMemberList(state.item?.member_group?.id).then((res) => {

        // Properly structure the owner object
        const owner = {
          id: null, // Owner's member ID can be set to null or a default
          member_group_id: state.item?.member_group?.id,
          status: "approved",
          role: "owner",
          user: state.item?.member_group?.owner || {}, // Include the owner details
        };

        // Combine members and owner
        const members = [...res.member_group_users, owner];

        // Filter out users based on the criteria
        const usersArray = members.filter((member: any) =>
          !state.users?.some((u: any) => u?.privater_type === member?.user?.class_name && u?.privater_id === member?.user?.id) &&
          member?.user?.id !== user?.id // Exclude the logged-in user
        );

        setPostUsers(usersArray);
        setLoading(false);
      });
    }
    getImageSrc()
  }, [state.item, state.users, user]);

  useEffect(() => {
    searchUsersOnline();
  }, [searchTerm]);

  const searchUsersOnline = async () => {
    if (!searchTerm) {
      setFilteredOptions([]);
      return;
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();
    try {
      setSearching(true);

      const groupId = state.item.member_group ? state.item.member_group.id : null

      const res = await appServices.searchUsers(
        searchTerm,
        groupId,
        cancelTokenRef.current.token,
        groupId ? false : true
      )

      const users = res.users
        .filter((searchUser: any) => searchUser.id !== user.id)
      const navigators = res.navigators

      setFilteredOptions(users.concat(navigators));
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setSearching(false);
    }
  };

  const displayRole = (userData: any) => {
    if (!state.item?.member_group) return
    let role;
    if (isGroupLeader(state.item?.member_group, userData)) {
      role = "leader"
    } else {
      role = postUsers?.find((member: any) => userData.id === member.user.id)?.role
    }

    if (!role || isMemberRole(role)) return ""

    return getRole(role)
  }

  const groupMemberRoleFilter = (isMember: boolean) => {
    if (!state.item?.member_group) return []
    const filteredGroupMember = postUsers.filter((member: any) => {
      return isMember ? isMemberRole(member.role) : !isMemberRole(member.role)
    })

    const fetchUsers = filteredGroupMember.filter((member: any) => {
      if (filteredOptions.some((filteredMember: any) => filteredMember.id === member.user.id)) return member
    })

    if (isMember) return fetchUsers

    return fetchUsers.sort((a: any, b: any) => {
      if (isGroupLeader(state.item?.member_group, a) && !isGroupLeader(state.item?.member_group, b)) {
        return -1
      } else if (!isGroupLeader(state.item?.member_group, a) && isGroupLeader(state.item?.member_group, b)) {
        return 1
      } else if (isSubLeaderRole(a.role) && !isSubLeaderRole(b.role)) {
        return -1
      } else if (!isSubLeaderRole(a.role) && isSubLeaderRole(b.role)) {
        return 1
      } else {
        return a.id - b.id
      }
    })
  }

  const handleAddUser = async (addUser: any) => {
    try {
      await appServices.addUserInPost(state.item.id, addUser?.id, addUser?.class_name)
      setShowMessage(`${T("add_user_added")}`)
      setShowSuccessModal(true)
      setFilteredOptions(filteredOptions.filter(
        (searchUser: any) => searchUser.class_name !== addUser?.class_name || searchUser.id !== addUser?.id)
      )
      setPostUsers((prev: any) => prev.filter((item: any) => item.user.id !== addUser?.id))
      return true
    } catch (error: any) {
      console.error(error)
      setShowMessage(error?.response?.data?.message ?? `${T("add_user_cannot_add")}`)
      setShowErrorModal(true)
      return false
    }
  }

  const closeModal = () => {
    setShowSuccessModal(false)
    setShowErrorModal(false)
    setShowMessage("")
  }

  const displayOption = (userData: any) => {
    const isExists = state?.users?.some((privateUser: any) =>
      privateUser.privater_type === userData?.class_name && privateUser.privater_id === userData?.id
    )

    return (
      <Box key={`${userData.class_name}_${userData.id}`} sx={styles.itemContainer}>
       <Avatar sx={styles.userImage} src={convertUserImage(userData?.image)} />
        <p style={{ ...styles.nameText, fontWeight: "normal" }}>{userData?.nickname}</p>
        {state.item?.member_group &&
          <Typography color="text.secondary">
            {displayRole(userData)}
          </Typography>
        }
        <SubmitButton
          validate={async () => true}
          onSubmit={() => handleAddUser(userData)}
        >
          {!isExists && <AddCircleOutlineIcon sx={styles.addIcon} />}
        </SubmitButton>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ padding: "1px 16px", paddingTop: "100px", borderBottom: "1px solid #ECEFF1" }}>
        <GCBackButton onClick={() => navigate(-1)} />
        <Box sx={styles.rowCenter}>
          <Avatar sx={styles.image} src={iconImage} />
          <p style={styles.nameText}>{state.item.member_group ? T("group_member") :getName()}</p>
        </Box>
      </Box>
      <Box sx={{ padding: "16px" }}>
        <Box sx={styles.searchBoxStyle}>
          <Typography sx={{ mb: 1 }}>{T("can_search_for_users")}</Typography>
          <TextField
            sx={{ width: "100%" }}
            id="outlined-basic"
            label={T("add_user_search_users")}
            variant="outlined"
            placeholder={`${T("add_user_search_users")}`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{searching && <CircularProgress size={20} />}</InputAdornment>,
            }}
          />
        </Box>
        <Typography style={styles.titleSeprator}>{T("post_private_users")}</Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
            <CircularProgress size={25} />
            <Typography sx={{ marginLeft: "10px", color: colors.farm_gray200 }}>{T("loading")}</Typography>
          </Box>
        ) : (
          <>{state.item?.member_group ? (
            <>
              {groupMemberRoleFilter(false).length > 0 &&
                <div style={styles.representative}>
                  <Typography sx={{ textAlign: "left", marginLeft: "15px" }}>{T("members_with_roles")}</Typography>
                  {groupMemberRoleFilter(false).map((member: any) => (
                    displayOption(member.user)
                  ))}
                </div>
              }
              {groupMemberRoleFilter(true).length > 0 &&
                <div>
                  <Typography sx={{ textAlign: "left", marginLeft: "15px" }}>{T("member")}</Typography>
                  {groupMemberRoleFilter(true).map((member: any) => (
                    displayOption(member.user)
                  ))}
                </div>
              }
            </>
          ) : (
            <Box>
              {filteredOptions.map((item: any) => (
                displayOption(item)
              ))}
            </Box>
          )}</>
        )}
      </Box>
      <MessageModal open={showSuccessModal} onClose={() => closeModal()} message={showMessage} />
      <ErrorMessageModal open={showErrorModal} onClose={() => closeModal()} messages={[showMessage]} />
    </Box>
  );
};

const styles = {
  headerBox: {
    padding: "1px 16px",
    paddingTop: "100px",
  },
  image: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  userImage: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    objectFit: "cover",
    marginLeft: "10px",
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    marginLeft: "10px",
    marginBottom: "0px",
    flex: 1,
  },
  itemContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "16px",
    marginBottom: "16px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    padding: "5px 0px",
  },
  rowCenter: {
    margin: "10px 0px",
    display: "flex",
    alignItems: "center",
  },
  titleSeprator: {
    borderBottomColor: colors.farm_green500,
    borderBottomWidth: 2,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    fontFamily: "mplus-1c-bold",
    marginBottom: 6,
  },
  addIcon: {
    color: colors.farm_green500,
    fontSize: "35px",
    cursor: "pointer",
    marginLeft: "10px",
  },
  searchBoxStyle: { padding: "10px 0", width: "100%", marginTop: "10px" },
  representative: { margin: "0 0 50px 0" }
};
export default AddPostUser;
